import type {
  BuilderEdge,
  BuilderNode,
  BuilderNodeType,
  ReadinessIssue,
  SavedFlow,
} from "@/shared/builder-session";
import { flowopsFetch } from "@/shared/builder-session/flowops-api-url";

export function createBuilderNode(
  type: BuilderNodeType,
  position: { x: number; y: number },
): BuilderNode {
  const id = `node-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const defaults: Record<
    "mcp" | "javascript" | "condition" | "loop" | "end",
    { name: string; description: string; config: Record<string, unknown> }
  > = {
    mcp: {
      name: "Tool",
      description: "Run an integration tool",
      config: { toolCall: { mode: "direct", toolName: "", arguments: {}, sideEffect: "read" } },
    },
    javascript: {
      name: "Code",
      description: "Transform data with JavaScript",
      config: { code: "return input;" },
    },
    condition: {
      name: "Condition",
      description: "Branch when an expression is true",
      config: { condition: "input.value == true", trueStep: null, falseStep: null },
    },
    loop: {
      name: "Loop",
      description: "Run for each item",
      config: {
        items: "{{input.items}}",
        itemVariable: "item",
        indexVariable: "index",
        maxIterations: 100,
        steps: [],
      },
    },
    end: {
      name: "End",
      description: "End of path",
      config: { endKind: "completed", reason: "" },
    },
  };
  const value =
    type in defaults
      ? defaults[type as keyof typeof defaults]
      : {
          name: type.charAt(0).toUpperCase() + type.slice(1),
          description: "",
          config: {},
        };
  return {
    id,
    type,
    position,
    name: value.name,
    config: value.config,
    stepMode: "source",
    inputs: {},
    nextStep: "end",
    data: value,
  };
}

const SUPPORTED_NODE_TYPES = new Set<BuilderNodeType>([
  "mcp",
  "javascript",
  "condition",
  "loop",
  "end",
  "trigger",
  "action",
  "notification",
]);

export function normalizeNode(raw: unknown, index = 0): BuilderNode | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.id !== "string") return null;
  const rawData =
    value.data && typeof value.data === "object" ? (value.data as Record<string, unknown>) : {};
  const rawType = String(value.type ?? rawData.type ?? "");
  const type = (
    rawType === "tool" ? "mcp" : rawType === "code" ? "javascript" : rawType
  ) as BuilderNodeType;
  if (!SUPPORTED_NODE_TYPES.has(type)) return null;
  const rawConfig =
    value.config && typeof value.config === "object"
      ? (value.config as Record<string, unknown>)
      : rawData.config && typeof rawData.config === "object"
        ? (rawData.config as Record<string, unknown>)
        : {};
  const config = rawConfig;
  const position =
    value.position && typeof value.position === "object"
      ? (value.position as { x: number; y: number })
      : { x: (index % 3) * 260, y: Math.floor(index / 3) * 160 };
  const name = String(value.name ?? rawData.name ?? value.label ?? rawData.label ?? type);
  return {
    ...(value as unknown as BuilderNode),
    id: value.id,
    type,
    position,
    name,
    config,
    data: {
      ...rawData,
      name,
      description: String(rawData.description ?? ""),
      config,
    },
  };
}

export function sanitizeHandleId(handle: string | null | undefined): string | undefined {
  if (!handle || handle === "null" || handle === "undefined") return undefined;
  return handle;
}

export function sanitizeEdgeForReactFlow(edge: BuilderEdge, sourceNodeType?: string): BuilderEdge {
  const sanitized: BuilderEdge = {
    ...edge,
    sourceHandle: sanitizeHandleId(edge.sourceHandle),
    targetHandle: sanitizeHandleId(edge.targetHandle),
  };
  if (sourceNodeType && sourceNodeType !== "condition") {
    delete sanitized.sourceHandle;
  }
  return sanitized;
}

export function sanitizeWorkflowEdges(
  edges: BuilderEdge[],
  nodes: Array<{ id: string; type?: string }>,
): BuilderEdge[] {
  const nodeTypes = new Map(nodes.map((node) => [node.id, node.type]));
  return edges.map((edge) => sanitizeEdgeForReactFlow(edge, nodeTypes.get(edge.source)));
}

export function normalizeEdge(raw: unknown, sourceNodeType?: string): BuilderEdge | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.source !== "string" || typeof value.target !== "string") return null;
  return sanitizeEdgeForReactFlow(
    {
      ...(value as unknown as BuilderEdge),
      id: typeof value.id === "string" ? value.id : `edge-${value.source}-${value.target}`,
      source: value.source,
      target: value.target,
      animated: value.animated !== false,
    },
    sourceNodeType,
  );
}

export function syncRouting(nodes: BuilderNode[], edges: BuilderEdge[]): BuilderNode[] {
  return nodes.map((node) => {
    const incoming = edges.filter((edge) => edge.target === node.id);
    const outgoing = edges.filter((edge) => edge.source === node.id);
    const config = { ...(node.config ?? node.data.config) };
    if (node.type === "condition") {
      config.trueStep =
        outgoing.find((edge) => (edge.sourceHandle ?? "true") === "true")?.target ?? null;
      config.falseStep = outgoing.find((edge) => edge.sourceHandle === "false")?.target ?? null;
    }
    return {
      ...node,
      name: node.data.name,
      config,
      stepMode: incoming.length ? "transform" : "source",
      nextStep:
        node.type === "end"
          ? "end"
          : node.type === "condition"
            ? "next"
            : outgoing.length === 0
              ? "end"
              : outgoing.length === 1
                ? outgoing[0].target
                : outgoing.map((edge) => edge.target),
      data: { ...node.data, config },
    };
  });
}

export function validateFlow(nodes: BuilderNode[], edges: BuilderEdge[]): ReadinessIssue[] {
  const issues: ReadinessIssue[] = [];
  if (!nodes.length) return [{ message: "Add at least one node." }];
  const ids = new Set(nodes.map((node) => node.id));
  for (const edge of edges) {
    if (!ids.has(edge.source) || !ids.has(edge.target)) {
      issues.push({ message: `Connection ${edge.id} points to a missing node.` });
    }
    if (edge.source === edge.target) {
      issues.push({ nodeId: edge.source, message: "A node cannot connect to itself." });
    }
  }
  for (const node of nodes) {
    const config = node.config ?? node.data.config;
    if (!node.data.name.trim()) issues.push({ nodeId: node.id, message: "Node name is required." });
    if (node.type === "mcp") {
      const toolCall = config.toolCall as { toolName?: string } | undefined;
      if (!toolCall?.toolName?.trim()) {
        issues.push({ nodeId: node.id, message: `${node.data.name}: choose a tool name.` });
      }
    }
    if (node.type === "javascript" && !String(config.code ?? "").trim()) {
      issues.push({ nodeId: node.id, message: `${node.data.name}: code is required.` });
    }
    if (node.type === "condition") {
      if (!String(config.condition ?? "").trim()) {
        issues.push({ nodeId: node.id, message: `${node.data.name}: condition is required.` });
      }
      const outgoing = edges.filter((edge) => edge.source === node.id);
      if (!outgoing.some((edge) => (edge.sourceHandle ?? "true") === "true")) {
        issues.push({ nodeId: node.id, message: `${node.data.name}: connect the Yes branch.` });
      }
      if (!outgoing.some((edge) => edge.sourceHandle === "false")) {
        issues.push({ nodeId: node.id, message: `${node.data.name}: connect the No branch.` });
      }
    }
    if (node.type === "loop" && !String(config.items ?? "").trim()) {
      issues.push({ nodeId: node.id, message: `${node.data.name}: items expression is required.` });
    }
  }
  return issues;
}

export function toSavedFlow(raw: unknown): SavedFlow | null {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  const id = String(value.id ?? value.workflowId ?? "").trim();
  if (!id) return null;
  const nodes = Array.isArray(value.steps)
    ? value.steps
        .map((node, index) => normalizeNode(node, index))
        .filter((node): node is BuilderNode => Boolean(node))
    : Array.isArray(value.nodes)
      ? value.nodes
          .map((node, index) => normalizeNode(node, index))
          .filter((node): node is BuilderNode => Boolean(node))
      : [];
  const edges = Array.isArray(value.edges)
    ? sanitizeWorkflowEdges(
        value.edges
          .map((edge) => normalizeEdge(edge))
          .filter((edge): edge is BuilderEdge => Boolean(edge)),
        nodes,
      )
    : [];
  return {
    id,
    name: String(value.name ?? "Untitled Workflow"),
    nodes,
    edges,
    deployedAgentId: String(value.agentId ?? value.deployedAgentId ?? ""),
    updatedAt: String(value.updatedAt ?? new Date().toISOString()),
    ...(Array.isArray(value.inputs) ? { inputs: value.inputs as SavedFlow["inputs"] } : {}),
    ...(value.inputDefaults && typeof value.inputDefaults === "object"
      ? { inputDefaults: value.inputDefaults as Record<string, unknown> }
      : {}),
    ...(typeof value.runUrl === "string" ? { runUrl: value.runUrl } : {}),
  };
}

export interface DraftSummary {
  id: string;
  name: string;
  nodeCount: number;
  updatedAt: string;
  /** Builder chat session to resume when continuing this draft. */
  sessionId?: string;
  /** building = mid-flow; ready = finalized, awaiting Deploy. */
  stage?: "building" | "ready";
  /** Toolkit / connection names used in the draft graph. */
  systems?: string[];
}

export async function listDraftFlows(options?: {
  limit?: number;
  offset?: number;
  q?: string;
  signal?: AbortSignal;
}): Promise<{ items: DraftSummary[]; total: number; hasMore: boolean }> {
  const params = new URLSearchParams({
    drafts: "1",
    limit: String(options?.limit ?? 20),
    offset: String(options?.offset ?? 0),
  });
  if (options?.q?.trim()) params.set("q", options.q.trim());

  const response = await flowopsFetch(`/api/workflows?${params.toString()}`, {
    signal: options?.signal,
  });
  if (!response.ok) {
    throw new Error(await response.text().catch(() => "Failed to list drafts"));
  }

  const payload = (await response.json()) as {
    items?: Array<{
      id?: string;
      name?: string;
      nodeCount?: number;
      updatedAt?: string;
      systems?: string[];
    }>;
    total?: number;
    hasMore?: boolean;
  };

  const items = (payload.items ?? [])
    .map((row): DraftSummary | null => {
      const id = String(row.id ?? "").trim();
      if (!id) return null;
      const systems = Array.isArray(row.systems)
        ? row.systems.map((s) => String(s).trim()).filter(Boolean)
        : [];
      return {
        id,
        name: String(row.name ?? "Untitled Workflow"),
        nodeCount: Number(row.nodeCount ?? 0),
        updatedAt: String(row.updatedAt ?? new Date().toISOString()),
        stage: "building",
        ...(systems.length > 0 ? { systems } : {}),
      };
    })
    .filter((row): row is DraftSummary => Boolean(row));

  const rawTotal = Number(payload.total ?? items.length);
  return {
    items,
    // -1 from later pages means "keep the first-page total" on the client.
    total: Number.isFinite(rawTotal) ? rawTotal : items.length,
    hasMore: Boolean(payload.hasMore),
  };
}

export async function getSavedFlow(id: string): Promise<SavedFlow> {
  const response = await flowopsFetch(`/api/workflows/${encodeURIComponent(id)}`);
  if (!response.ok) {
    throw new Error(await response.text().catch(() => "Failed to load workflow"));
  }
  const saved = toSavedFlow(await response.json());
  if (!saved) throw new Error("Invalid workflow response");
  return saved;
}

export async function persistFlow(flow: SavedFlow): Promise<SavedFlow> {
  const body = {
    name: flow.name,
    steps: flow.nodes,
    edges: flow.edges,
    agentId: flow.deployedAgentId || undefined,
    ...(flow.inputs?.length ? { inputs: flow.inputs } : {}),
    ...(flow.inputDefaults ? { inputDefaults: flow.inputDefaults } : {}),
  };

  const existing = await flowopsFetch(`/api/workflows/${encodeURIComponent(flow.id)}`);
  const response = existing.ok
    ? await flowopsFetch(`/api/workflows/${encodeURIComponent(flow.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
    : await flowopsFetch("/api/workflows", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, workflowId: flow.id }),
      });

  if (!response.ok) {
    throw new Error(await response.text().catch(() => "Failed to save workflow"));
  }
  const saved = toSavedFlow(await response.json());
  if (!saved) throw new Error("Invalid save response");
  return saved;
}
